import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import { StatusBadge } from '../components/ui/status-badge';
import { CreditsDropdown } from '../components/ui/credits-dropdown';
import { RiPhoneFill } from '@remixicon/react';
import { usageService } from '../services/usageService';
import { agentsService } from '../services/agentsService';
import { conversationService } from '../services/conversationService';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from '../components/ui/chart';
import { authService } from '../services/authService';
import { db } from '../config/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

function Dashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCalls] = useState(0); // Fixed value - no random numbers
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agentsLoading, setAgentsLoading] = useState(false);
  
  // User data - fetched from Firestore
  const [user, setUser] = useState({
    name: 'User', // Will be updated from Firestore
    totalCredits: 10000,
    remainingCredits: 7500
  });
  const [userLoading, setUserLoading] = useState(true);

  // Fetch user's full name from Firestore
  const fetchUserData = useCallback(async () => {
    try {
      setUserLoading(true);
      const currentUser = authService.getCurrentUser();
      
      if (currentUser && currentUser.email) {
        // Get user document from Firestore using email as document ID
        const userDocRef = doc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(prevUser => ({
            ...prevUser,
            name: userData.fullName || userData.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
          }));
        } else {
          // Fallback to auth user data if Firestore document doesn't exist
          setUser(prevUser => ({
            ...prevUser,
            name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Fallback to default name
      setUser(prevUser => ({
        ...prevUser,
        name: 'User'
      }));
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Helper functions for formatting (copied from conversations page)

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTimeOnly = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleRowClick = (conversationId) => {
    navigate(`/conversations/${conversationId}`);
  };

  // Helper function to get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const response = await agentsService.getAgents();
      const agentsList = response.agents || [];
      
      // Add "All Agents" option at the beginning
      const agentsWithAll = [
        { agent_id: 'all', name: 'All Agents' },
        ...agentsList
      ];
      
      setAgents(agentsWithAll);
    } catch (err) {
      console.error('Error fetching agents:', err);
      // Set default with just "All Agents" option
      setAgents([{ agent_id: 'all', name: 'All Agents' }]);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  // Helper function to get Unix timestamps for date ranges
  const getDateRange = useCallback((range) => {
    const now = new Date();
    
    let daysBack;
    switch (range) {
      case 'day':
        daysBack = 1;
        break;
      case 'week':
        daysBack = 7;
        break;
      case 'month':
        daysBack = 30;
        break;
      case 'quarter':
        daysBack = 90;
        break;
      default:
        daysBack = 30;
    }
    
    // Set end time to 23:59:59 of the current day (end of day)
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);
    const endUnix = endDate.getTime();
    
    // Set start time to 00:00:00 of the start day (beginning of day)
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - daysBack);
    startDate.setHours(0, 0, 0, 0);
    const startUnix = startDate.getTime();
    
    return { startUnix, endUnix };
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      const { startUnix, endUnix } = getDateRange(dateRange);
      
      // Prepare options with agent filtering if not "all"
      const baseOptions = {
        metric: 'request_count',
        breakdown_type: 'none',
        aggregation_interval: 'cumulative'
      };
      
      // Add agent filtering if specific agent is selected
      if (selectedAgent !== 'all') {
        baseOptions.agent_id = selectedAgent;
      }
      
      // Fetch time-series data for chart and cumulative data for metrics
      const [totalCallsData, minutesUsedData, totalCreditsData] = await Promise.all([
        // Total calls (requests) - cumulative
        usageService.getCharacterStats(startUnix, endUnix, baseOptions),
        
        // Minutes used - cumulative
        usageService.getCharacterStats(startUnix, endUnix, {
          ...baseOptions,
          metric: 'minutes_used'
        }),
        
        // Total credits used - cumulative
        usageService.getCharacterStats(startUnix, endUnix, {
          ...baseOptions,
          metric: 'credits'
        }),
        
      ]);
      
      // Sum the arrays to get accumulated values
      const totalCalls = totalCallsData.usage?.All?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      const totalMinutes = minutesUsedData.usage?.All?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      const totalCredits = totalCreditsData.usage?.All?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      
      // Calculate average minutes per call
      const averageMinutesPerCall = totalCalls > 0 ? (totalMinutes / totalCalls) : 0;
      
      // Calculate average cost per call
      const averageCostPerCall = totalCalls > 0 ? (totalCredits / totalCalls) : 0;
      
      // Process chart data with mock data
      const processedChartData = [
        { date: 'Jan 2024', successRate: 85 },
        { date: 'Feb 2024', successRate: 88 },
        { date: 'Mar 2024', successRate: 92 },
        { date: 'Apr 2024', successRate: 89 },
        { date: 'May 2024', successRate: 94 },
        { date: 'Jun 2024', successRate: 91 },
        { date: 'Jul 2024', successRate: 96 },
        { date: 'Aug 2024', successRate: 93 },
        { date: 'Sep 2024', successRate: 97 },
        { date: 'Oct 2024', successRate: 95 },
        { date: 'Nov 2024', successRate: 98 },
        { date: 'Dec 2024', successRate: 96 }
      ];
      
      // Set analytics data in expected format
      setAnalytics({
        count: totalCalls,
        average_minutes_per_call: averageMinutesPerCall,
        total_credits: totalCredits,
        average_cost_per_call: averageCostPerCall,
        metrics: processedChartData
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, getDateRange, selectedAgent]);

  // Fetch recent conversations
  const fetchConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const conversationsData = await conversationService.getConversations({
        limit: 10
      });

      const items = conversationsData.conversations || [];
      setConversations(items);
    } catch (err) {
      // Error fetching conversations
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchAnalytics();
    fetchConversations();
  }, [fetchUserData, fetchAnalytics, fetchConversations]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Set mock chart data immediately
  useEffect(() => {
    // Generate data for August-September 2025 with both success and failed rates
    const mockChartData = [];
    const startDate = new Date(2025, 7, 1); // August 1, 2025 (month is 0-indexed)
    const endDate = new Date(2025, 8, 30); // September 30, 2025
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      // Generate realistic success rates
      const baseSuccessRate = 85 + Math.random() * 10; // 85-95%
      const successRate = Math.round(baseSuccessRate);
      
      mockChartData.push({
        date: `${date.getMonth() + 1}/${date.getDate()}`, // Month/Day format
        dayNumber: date.getDate(), // Day of the month
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }), // Mon, Tue, etc.
        monthName: date.toLocaleDateString('en-US', { month: 'short' }), // Aug, Sep
        year: date.getFullYear(), // 2025
        successRate: successRate
      });
    }

    setAnalytics(prev => ({
      ...prev,
      metrics: mockChartData
    }));
  }, []);

  // Metrics data based on analytics
  const metricsData = [
    {
      title: "Total Calls",
      value: isLoading ? '-' : (analytics?.count || 0),
      change: analytics?.count > 0 ? { value: "+12.5%", type: "increase" } : undefined,
      description: "Number of calls this period"
    },
    {
      title: "Average Minutes Per Call",
      value: isLoading ? '-' : (analytics?.average_minutes_per_call || 0).toFixed(2),
      change: analytics?.average_minutes_per_call > 0 ? { value: "+8.2%", type: "increase" } : undefined,
      description: "Average minutes per call"
    },
    {
      title: "Average Cost Per Call",
      value: isLoading ? '-' : (analytics?.average_cost_per_call || 0).toFixed(4),
      change: analytics?.average_cost_per_call > 0 ? { value: "+5.1%", type: "increase" } : undefined,
      description: "Average credits per call"
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-4">Error loading dashboard</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchAnalytics}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background rounded-tl-lg -mr-8">
      {/* Header with enhanced styling */}
      <div className="bg-background border-b border-border sticky top-0 z-10 rounded-tl-lg">
        <div className="flex h-16 items-center justify-between px-8">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-card-foreground tracking-tight">Dashboard</h1>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 space-y-8 overflow-x-hidden">

        {/* Top Row: Active Calls Badge and Credits */}
        <div className="flex items-center gap-4">
          {/* Active Calls StatusBadge */}
          <StatusBadge 
            leftIcon={RiPhoneFill}
            leftLabel="Active calls"
            rightLabel={activeCalls.toString()}
            status="success"
          />

          {/* Credits Display */}
          <CreditsDropdown 
            totalCredits={analytics?.total_credits || 0}
            remainingCredits={Math.max(0, (analytics?.total_credits || 0) - Math.floor((analytics?.total_credits || 0) * 0.3))}
            usedCredits={Math.floor((analytics?.total_credits || 0) * 0.3)}
          />
        </div>

        {/* Greeting Message and Controls Row */}
        <div className="flex items-center justify-between">
          {/* Greeting Message */}
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground">{getGreeting()}</h2>
            <h1 className="text-2xl font-bold text-card-foreground">{user.name}</h1>
          </div>

          {/* Controls: Period Dropdown and Agents Combobox */}
          <div className="flex items-center space-x-4">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[180px] h-10 border-input bg-card text-card-foreground hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent className="border-border">
                <SelectItem value="day" className="text-card-foreground hover:bg-muted">Last day</SelectItem>
                <SelectItem value="week" className="text-card-foreground hover:bg-muted">Last 7 days</SelectItem>
                <SelectItem value="month" className="text-card-foreground hover:bg-muted">Last 30 days</SelectItem>
                <SelectItem value="quarter" className="text-card-foreground hover:bg-muted">Last 3 months</SelectItem>
              </SelectContent>
            </Select>
            <Combobox
              options={agents.map(agent => ({
                value: agent.agent_id,
                label: agent.name
              }))}
              value={selectedAgent}
              onValueChange={setSelectedAgent}
              placeholder="All Agents"
              searchPlaceholder="Search agents..."
              emptyText="No agents found."
              disabled={agentsLoading}
            />
          </div>
        </div>


        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            // Skeleton loading cards
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} className="border border-border bg-card shadow-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-8 w-16" />
                    </div>
                    <Skeleton className="h-6 w-12" />
                  </div>
                  <Skeleton className="h-3 w-32 mt-2" />
                </CardContent>
              </Card>
            ))
          ) : (
            metricsData.map((metric, index) => (
              <Card key={index} className="border border-border bg-card shadow-none hover:shadow-sm transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                      <p className="text-2xl font-bold text-card-foreground tracking-tight">
                        {metric.value}
                      </p>
                    </div>
                    {metric.change && (
                      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                        metric.change.type === 'increase' 
                          ? 'bg-chart-1/10 text-chart-1' 
                          : 'bg-destructive/10 text-destructive'
                      }`}>
                        <span>{metric.change.value}</span>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d={metric.change.type === 'increase' ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" : "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"} />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/80 mt-2">{metric.description}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>


        {/* Usage Chart */}
        <Card className="border border-border bg-card shadow-none">
          <CardHeader className="pb-6 border-b border-border">
            <div className="space-y-1">
              <CardTitle className="text-xl font-bold text-card-foreground tracking-tight">Success Rate</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="text-muted-foreground">Loading chart data...</div>
              </div>
            ) : analytics?.metrics?.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={analytics.metrics}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      interval="preserveStartEnd"
                      tick={{ fontSize: '12px', fill: 'var(--muted-foreground)' }}
                      tickFormatter={(value, index) => {
                        // Show every 5th day to see more progression
                        if (index % 5 === 0) {
                          // Calculate the date based on index and start date
                          const startDate = new Date(2025, 7, 1); // August 1, 2025
                          const currentDate = new Date(startDate);
                          currentDate.setDate(startDate.getDate() + index);
                          
                          const monthName = currentDate.toLocaleDateString('en-US', { month: 'short' });
                          const day = currentDate.getDate();
                          return `${monthName} ${day}`;
                        }
                        return '';
                      }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tick={{ fontSize: '12px', fill: 'var(--muted-foreground)' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Success Rate']}
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0] && payload[0].payload) {
                          const data = payload[0].payload;
                          // Handle both old and new data formats
                          if (data.monthName && data.dayNumber) {
                            // New format: {monthName: 'Aug', dayNumber: 1, dayName: 'Thu', ...}
                            return `${data.monthName} ${data.dayNumber}, 2025${data.dayName ? ` (${data.dayName})` : ''}`;
                          } else if (data.date && typeof data.date === 'string') {
                            // Old format: {date: 'Jun 2024', successRate: 91}
                            return `Aug 1, 2025`; // Default to Aug 1 for old data
                          } else {
                            // Fallback
                            return `Aug 1, 2025`;
                          }
                        }
                        return `Aug 1, 2025`;
                      }}
                      contentStyle={{
                        fontSize: '12px',
                        fontFamily: 'var(--font-sans)',
                        color: 'var(--foreground)',
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)'
                      }}
                    />
                    <Area
                      dataKey="successRate"
                      type="monotone"
                      fill="var(--chart-1)"
                      fillOpacity={0.4}
                      stroke="var(--chart-1)"
                      strokeWidth={2}
                      name="Success Rate"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center">
                <div className="text-muted-foreground">No usage data available for the selected period</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Conversations Table */}
        <Card className="border border-border bg-card shadow-none">
          <CardHeader className="pb-6 border-b border-border">
            <div className="space-y-1">
              <CardTitle className="text-xl font-bold text-card-foreground tracking-tight">Recent Conversations</CardTitle>
              <p className="text-sm text-muted-foreground">
                Last 10 conversations from your call history
              </p>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="text-muted-foreground font-semibold">Date</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Time</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Agent</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Duration</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Language</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversationsLoading ? (
                  // Skeleton loading rows
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[60px]" />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Skeleton className="h-10 w-10 rounded-xl" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-[120px]" />
                            <Skeleton className="h-3 w-[80px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[70px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-[80px] rounded-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : !conversations || conversations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 bg-muted flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-card-foreground mb-2">No conversations found</h3>
                        <p className="text-muted-foreground">
                          No conversation history found
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  conversations.slice(0, 10).map((conversation) => {
                    return (
                      <TableRow
                        key={conversation.conversation_id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                        onClick={() => handleRowClick(conversation.conversation_id)}
                      >
                        <TableCell className="font-medium text-card-foreground text-xs">
                          {formatDateOnly(conversation.start_time_unix_secs)}
                        </TableCell>
                        <TableCell className="font-medium text-card-foreground text-xs">
                          {formatTimeOnly(conversation.start_time_unix_secs)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-accent text-accent-foreground font-semibold">
                                {conversation.agent_name ? conversation.agent_name.charAt(0).toUpperCase() : 'A'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-semibold text-card-foreground">{conversation.agent_name}</div>
                              <div className="text-xs text-muted-foreground">
                                ID: {conversation.conversation_id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-card-foreground bg-secondary px-3 py-1.5 rounded-md">
                            {formatDuration(conversation.call_duration_secs)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-card-foreground">
                            English
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            conversation.call_successful === "success"
                              ? 'bg-success-background text-success-foreground'
                              : 'bg-destructive-background text-destructive'
                          }`}>
                            {conversation.call_successful === "success" ? "Successful" : "Failed"}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
